import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, SafeAreaView, ScrollView, Platform } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ModuleHeader, HotDestinationsBar, type HotDestinationItem } from '@cubemeet/ui';
import type { RootState } from '../store';
import { setReturnTo } from '@cubemeet/core/store/slices/authSlice';
import GlobalPosts from '../components/GlobalPosts';
import GlobalCards from '../components/GlobalCards';
import GlobalTrips from '../components/GlobalTrips';
import CardListPage from './CardListPage';
import { useTravelI18n } from '../modules/travel/useTravelI18n';
import { getHomeHotDestinations } from '@cubemeet/core/config/hotDestinations';

/**
 * 首页 - 主页，所有用户都可以访问
 * - 已登录用户：看到个性化内容 + 公开内容
 * - 未登录用户：看到公开内容 + 登录提示
 * 三段布局：ModuleHeader（头部） + 公开行程列表 + BottomTabBar（底部）
 */
const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTravelI18n();
  const authState = useSelector((state: RootState) => (state as any)?.auth ?? {}) as any;
  const isAuthenticated = Boolean(authState?.isAuthenticated);
  
  const handleLogin = () => {
    // 从首页的登录按钮进入，登录后返回首页
    navigate('/auth');
  };

  // 从共享配置获取热门景点数据
  const hotDestinationsConfig = getHomeHotDestinations(4);
  const hotDestinations: HotDestinationItem[] = hotDestinationsConfig.map(dest => ({
    key: dest.key,
    label: t(dest.labelKey, { default: dest.defaultLabel }),
    imageUrl: dest.webImageUrl,
    onPress: () => navigate(dest.webPath),
  }));

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.mainContainer}>
        <ModuleHeader
          moduleType="travel"
          title={isAuthenticated ? t('home.welcomeBack') : t('home.discoverTravel')}
          subtitle={t('home.discoverPlans')}
          showMiniCube
          topInset={0}
          showCreateButton={false}
        />

        <ScrollView 
          style={styles.scrollView} 
          contentContainerStyle={styles.contentContainer} 
          showsVerticalScrollIndicator={false}
        >
          {/* 热门景点栏和登录提示 */}
          <View style={styles.contentInner}>
            <View style={styles.form}>
              {/* 未登录时显示登录提示 */}
              {!isAuthenticated && (
                <View style={styles.loginPrompt}>
                  <Text style={styles.loginPromptText}>{t('home.loginPrompt')}</Text>
                  <TouchableOpacity
                    style={styles.loginPromptButton}
                    onPress={handleLogin}
                    activeOpacity={0.85}
                  >
                    <Text style={styles.loginPromptButtonText}>{t('home.loginButton')}</Text>
                  </TouchableOpacity>
                </View>
              )}

              {/* 热门景点栏 */}
              <HotDestinationsBar
                title={t('home.whereToGo')}
                moreText={t('home.viewMore')}
                items={hotDestinations}
                onMorePress={() => navigate('/city')}
                style={[styles.hotDestinations, { borderBottomWidth: 0, paddingVertical: 12 }]}
              />
            </View>
          </View>

          {/* 推荐卡片区 */}
          <View style={styles.contentInner}>
            <View style={styles.form}>
              <CardListPage viewMode="grid" title={t('home.recommendedCards')} embedded />
            </View>
          </View>

          {/* 中间内容区域 - 最新游记 */}
          <View style={styles.contentInner}>
            <View style={[styles.form, { paddingHorizontal: 16 }]}>
              <View style={styles.moduleHeaderRow}>
                <View style={styles.moduleTitleBar} />
                <Text style={styles.moduleTitleText}>{t('home.latestPosts')}</Text>
              </View>

              <View style={styles.contentTabSection}>
                <GlobalPosts />
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  mainContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 20,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
      default: {
        elevation: 4,
      }
    }),
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    paddingVertical: 0,
  },
  searchBar: {
    backgroundColor: '#FFFFFF',
  },
  hotDestinations: {
    backgroundColor: '#FFFFFF',
  },
  contentInner: {
    flex: 0,
    width: '100%',
    alignItems: 'center',
  },
  form: {
    width: '100%',
    alignSelf: 'center',
  },
  
  loginPrompt: {
    backgroundColor: '#EFF6FF',
    borderRadius: 12,
    padding: 16,
    marginTop: 20, // 添加与头部的间距
    marginBottom: 24,
    marginHorizontal: 20, // 添加左右边距，与内容区域保持一致
    borderWidth: 1,
    borderColor: '#BFDBFE',
    alignItems: 'center',
  },
  loginPromptText: {
    fontSize: 14,
    color: '#1E40AF',
    fontWeight: '600',
    marginBottom: 12,
    textAlign: 'center',
  },
  loginPromptButton: {
    backgroundColor: '#2563EB',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  loginPromptButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  // ===== 标签页 =====
  tabsSection: {
    flexDirection: 'row',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    gap: 8,
    marginBottom: 16,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabButtonActive: {
    borderBottomColor: '#ff6b35',
  },
  tabButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#999',
  },
  tabButtonTextActive: {
    color: '#ff6b35',
  },

  // ===== 内容区 =====
  contentTabSection: {
    paddingVertical: 12,
    marginBottom: 32,
  },

  travelsSection: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 16,
  },
  moduleHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 0,
    marginBottom: 12,
    paddingTop: 8,
    paddingBottom: 4,
  },
  moduleTitleBar: {
    width: 4,
    height: 20,
    backgroundColor: '#ff6b35',
    borderRadius: 2,
    marginRight: 10,
    marginLeft: 4,
  },
  moduleTitleText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  cardWrapper: {
    marginBottom: 12,
  },
  cardListWrapper: {
    marginBottom: 24,
  },
  cardListFullWidth: {
    width: '100%',
    backgroundColor: '#fff',
    // Ensure it sits above the page background and stretches full width
    alignSelf: 'stretch',
    // Optional: keep content centered with same max width as other modules
    paddingVertical: 16,
  },
});

export default HomePage;
